import hotToast from 'react-hot-toast';
import { keyframes } from 'goober';
import React from 'react';

type ToastVariant = 'default' | 'destructive' | 'success';

type ToasterToast = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  variant?: ToastVariant;
  duration?: number;
  className?: string;
};

interface State {
  toasts: ToasterToast[];
}

type Toast = Omit<ToasterToast, 'id'>;
type ToastFunction = ((props: Toast) => {
  id: string;
  dismiss: () => void;
  update: (nextProps: Toast) => string;
}) & {
  success: (message: React.ReactNode, options?: Record<string, any>) => string;
  error: (message: React.ReactNode, options?: Record<string, any>) => string;
  dismiss: (toastId?: string) => void;
};

const isSuccessTitle = (title: React.ReactNode): boolean =>
  typeof title === 'string' && ['thành công', 'success'].includes(title.trim().toLowerCase());

const shouldHideErrorTitle = (title: React.ReactNode, description: React.ReactNode, variant?: ToastVariant): boolean =>
  variant === 'destructive' &&
  Boolean(description) &&
  typeof title === 'string' &&
  title.trim().toLowerCase() === 'lỗi';

const ToastContent = ({ title, description, action, variant }: Toast) => {
  if (!title && !description && !action) return null;

  const visibleTitle = shouldHideErrorTitle(title, description, variant) ? null : title;
  const descriptionClassName = visibleTitle
    ? 'mt-1 overflow-hidden whitespace-pre-line text-sm leading-5 text-slate-600'
    : 'overflow-hidden whitespace-pre-line text-sm font-medium leading-5 text-slate-800';

  const isSuccess =
    variant === 'success' ||
    (typeof title === 'string' && isSuccessTitle(title)) ||
    (typeof description === 'string' &&
      (description.toLowerCase().includes('thành công') || description.toLowerCase().includes('success')));

  const isDestructive = variant === 'destructive';

  return React.createElement(
    'div',
    { className: 'flex min-w-0 max-w-[420px] items-start gap-2.5' },
    isSuccess
      ? React.createElement(
          'div',
          {
            className:
              'mt-[2px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white'
          },
          React.createElement(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '12',
              height: '12',
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: '4',
              strokeLinecap: 'round',
              strokeLinejoin: 'round'
            },
            React.createElement('polyline', { points: '20 6 9 17 4 12' })
          )
        )
      : null,
    null,
    React.createElement(
      'div',
      { className: 'min-w-0 flex-1' },
      visibleTitle
        ? React.createElement('div', { className: 'text-sm font-semibold leading-5 text-slate-950' }, visibleTitle)
        : null,
      description ? React.createElement('div', { className: descriptionClassName }, description) : null
    ),
    action ? React.createElement('div', { className: 'shrink-0' }, action) : null
  );
};

let shouldReduceMotion: boolean | undefined = undefined;

const prefersReducedMotion = (): boolean => {
  if (shouldReduceMotion === undefined && typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    shouldReduceMotion = !mediaQuery || mediaQuery.matches;
  }
  return shouldReduceMotion ?? false;
};

const enterAnimation = (factor: number) => `
  0% {transform: translate3d(0,${factor * -200}%,0) scale(.6); opacity:.5;}
  100% {transform: translate3d(0,0,0) scale(1); opacity:1;}
`;

const exitAnimation = (factor: number) => `
  0% {transform: translate3d(0,0,-1px) scale(1); opacity:1;}
  100% {transform: translate3d(0,${factor * -150}%,-1px) scale(.6); opacity:0;}
`;

const fadeInAnimation = `0%{opacity:0;} 100%{opacity:1;}`;
const fadeOutAnimation = `0%{opacity:1;} 100%{opacity:0;}`;

const getAnimationStyle = (visible: boolean): React.CSSProperties => {
  const factor = -1; // bottom position has a factor of -1 in react-hot-toast

  const [enter, exit] = prefersReducedMotion()
    ? [fadeInAnimation, fadeOutAnimation]
    : [enterAnimation(factor), exitAnimation(factor)];

  return {
    animation: visible
      ? `${keyframes(enter)} 0.35s cubic-bezier(.21,1.02,.73,1) forwards`
      : `${keyframes(exit)} 0.4s forwards cubic-bezier(.06,.71,.55,1)`
  };
};

const ApiErrorToastContent = ({ description, t }: Toast & { t?: any }) => {
  const isVisible = t ? t.visible : true;
  const animationStyle = getAnimationStyle(isVisible);

  return React.createElement(
    'div',
    {
      className:
        'flex w-[min(440px,calc(100vw-32px))] max-w-full items-start gap-3 rounded-lg border border-red-200 bg-white px-3 py-3 text-slate-950 shadow-xl shadow-red-950/10 pointer-events-auto',
      style: animationStyle
    },
    React.createElement(
      'div',
      {
        className: 'mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-white'
      },
      React.createElement(
        'svg',
        {
          xmlns: 'http://www.w3.org/2000/svg',
          width: '10',
          height: '10',
          viewBox: '0 0 24 24',
          fill: 'none',
          stroke: 'currentColor',
          strokeWidth: '4',
          strokeLinecap: 'round',
          strokeLinejoin: 'round'
        },
        React.createElement('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
        React.createElement('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
      )
    ),
    React.createElement('div', { className: 'min-w-0 flex-1' }, description),
    t
      ? React.createElement(
          'button',
          {
            onClick: () => hotToast.dismiss(t.id),
            className: 'shrink-0 text-slate-400 hover:text-slate-600 rounded transition p-0.5 mt-[1px]',
            title: 'Đóng'
          },
          React.createElement(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '16',
              height: '16',
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: '2',
              strokeLinecap: 'round',
              strokeLinejoin: 'round'
            },
            React.createElement('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
            React.createElement('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
          )
        )
      : null
  );
};

const toast: ToastFunction = ({ ...props }) => {
  const isSuccess =
    props.variant === 'success' ||
    (typeof props.title === 'string' && isSuccessTitle(props.title)) ||
    (typeof props.description === 'string' &&
      (props.description.toLowerCase().includes('thành công') || props.description.toLowerCase().includes('success')));

  const content = React.createElement(ToastContent, {
    ...props,
    variant: props.variant === 'destructive' ? 'destructive' : isSuccess ? 'success' : 'default'
  });

  const options: Record<string, any> = {
    duration: props.duration,
    id: props.variant === 'destructive' ? 'global-error-toast' : undefined,
    ...(isSuccess && { icon: null })
  };
  if (props.className) {
    options.className = props.className;
  }

  const id =
    props.variant === 'destructive' && React.isValidElement(props.description)
      ? hotToast.custom(t => React.createElement(ApiErrorToastContent, { ...props, t }), options)
      : props.variant === 'destructive'
        ? hotToast.error(content, options)
        : isSuccess
          ? hotToast.success(content, options)
          : hotToast(content, options);

  const dismiss = () => hotToast.dismiss(id);
  const update = (nextProps: Toast) => {
    const nextIsSuccess =
      nextProps.variant === 'success' ||
      (typeof nextProps.title === 'string' && isSuccessTitle(nextProps.title)) ||
      (typeof nextProps.description === 'string' &&
        (nextProps.description.toLowerCase().includes('thành công') ||
          nextProps.description.toLowerCase().includes('success')));

    const updateOptions: Record<string, any> = {
      ...options,
      duration: nextProps.duration ?? props.duration,
      id
    };
    if (nextProps.className || props.className) {
      updateOptions.className = nextProps.className || props.className;
    }
    return hotToast.custom(
      React.createElement(ToastContent, {
        ...props,
        ...nextProps,
        variant: nextProps.variant === 'destructive' ? 'destructive' : nextIsSuccess ? 'success' : 'default'
      }),
      updateOptions
    );
  };

  return {
    id,
    dismiss,
    update
  };
};

toast.success = (message: React.ReactNode, options?: Record<string, any>) => {
  return toast({
    description: message,
    variant: 'success',
    ...options
  }).id;
};

toast.error = (message: React.ReactNode, options?: Record<string, any>) => {
  return toast({
    description: message,
    variant: 'destructive',
    ...options
  }).id;
};

toast.dismiss = (toastId?: string) => {
  hotToast.dismiss(toastId);
};

function useToast(): State & {
  toast: ToastFunction;
  dismiss: (toastId?: string) => void;
} {
  return {
    toasts: [],
    toast,
    dismiss: (toastId?: string) => hotToast.dismiss(toastId)
  };
}

export { useToast, toast };
